import React from 'react';
import { Box, Paper, Skeleton, Typography } from '@mui/material';
import DotWaveLoader from '../components/commonComponents/DotWaveLoader';
import { performanceInsetSx, performancePanelSx } from '../styles/performanceMsiStyles';
import { APP_STYLE_PALETTE } from '../styles/msiStyleTokens';

const panelSx = { border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5, minWidth: 0, ...performanceInsetSx };
const Bar = ({ width = '100%', height = 14 }) => <Skeleton aria-hidden="true" animation="wave" variant="rounded" width={width} height={height} />;

// Keep the real workspace's layout while its context or first response loads.
// Placeholder marks are decorative; the enclosing region announces busy state.
export default function HistoricalPerformanceSkeleton({ showViews = true, withinCard = false, title = 'Portfolio' }) {
    const workspace = <Box sx={{ display: 'grid', gap: 1.2, minWidth: 0 }}>
        <Box sx={panelSx}>
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, mb: 1.5 }}>
                <Typography variant="h6" sx={{ flex: 1 }}>{title}</Typography>
                <Bar width={190} height={28} />
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(3, minmax(0, 1fr))', lg: 'repeat(9, minmax(0, 1fr))' }, gap: 1 }}>
                {Array.from({ length: 9 }, (_, i) => <Box key={i} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 0.75, p: 1, display: 'grid', gap: 1, minWidth: 0 }}>
                    <Bar width="75%" height={11} /><Bar width="55%" height={20} /><Bar width="90%" height={10} />
                </Box>)}
            </Box>
        </Box>
        <Box sx={{ ...panelSx, p: 0, overflow: 'hidden' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, p: 1.5 }}>
                <Typography variant="subtitle2">Factor attribution</Typography>
                <Bar width={72} height={30} /><Bar width={180} height={30} />
                <Box sx={{ ml: 'auto', width: 180, maxWidth: '100%' }}><Bar height={30} /></Box>
            </Box>
            {Array.from({ length: 8 }, (_, i) => <Box key={i} sx={{ display: 'grid', gridTemplateColumns: 'minmax(60px, 1fr) minmax(80px, 4fr) minmax(40px, 0.6fr) minmax(40px, 0.6fr)', alignItems: 'center', gap: 2, px: 1.5, py: 1, borderTop: '1px solid', borderColor: 'divider', bgcolor: i % 2 ? APP_STYLE_PALETTE.surfaceSubtle : APP_STYLE_PALETTE.surface }}>
                <Bar width="75%" height={11} /><Bar width={`${85 - i * 6}%`} height={12} /><Bar height={11} /><Bar height={11} />
            </Box>)}
        </Box>
        <Box sx={{ ...panelSx, backgroundColor: APP_STYLE_PALETTE.surface }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}><Bar width={220} height={30} /><Bar width={130} height={30} /></Box>
            <DotWaveLoader height={260} decorative />
        </Box>
    </Box>;
    return <Box role="status" aria-label="Loading historical performance" aria-busy="true" sx={{ display: 'grid', gap: 1.5, minWidth: 0, '@media (prefers-reduced-motion: reduce)': { '& .MuiSkeleton-root, & .MuiSkeleton-root::after': { animation: 'none' } } }}>
        {showViews && <>
            <Paper aria-hidden="true" sx={{ ...performancePanelSx, p: 1.5, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                {[110, 96, 96, 96, 96].map((width, i) => <Bar key={i} width={width} height={32} />)}
            </Paper>
            <Box aria-hidden="true" sx={{ px: 1.5 }}><Bar width={100} height={30} /></Box>
        </>}
        {withinCard ? workspace : <Paper sx={{ ...performancePanelSx, p: { xs: 1, sm: 1.5 } }}>{workspace}</Paper>}
    </Box>;
}
